/**
 * Utility functions for safe error display in UI
 * Prevents [object Object] from appearing when errors are displayed
 */

export interface ErrorLike {
  message?: string;
  code?: string;
  details?: unknown;
}

/**
 * Safely extracts a human-readable error message from any error-like object
 */
export function getErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error occurred';
  
  if (typeof error === 'string') return error;
  
  if (error instanceof Error) return error.message;
  
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as ErrorLike;
    
    // Try common error message fields
    if (typeof errorObj.message === 'string' && errorObj.message.length > 0) {
      return errorObj.message;
    }
    
    // Try code field
    if (typeof errorObj.code === 'string' && errorObj.code.length > 0) {
      return `Error: ${errorObj.code}`;
    }
    
    // Try details field  
    if (typeof errorObj.details === 'string' && errorObj.details.length > 0) {
      return errorObj.details;
    }
    
    // For objects with custom toString
    if (typeof error.toString === 'function') {
      const stringified = error.toString();
      if (stringified !== '[object Object]') {
        return stringified;
      }
    }
    
    // Last resort: try to extract any string value
    const stringValues = Object.values(error).filter(v => typeof v === 'string' && v.length > 0);
    if (stringValues.length > 0) {
      return stringValues[0];
    }
  }
  
  return 'An error occurred';
}

/**
 * Safely extracts an error code from any error-like object
 */
export function getErrorCode(error: unknown): string | null {
  if (!error) return null;
  
  if (error instanceof Error && 'code' in error) {
    return typeof error.code === 'string' ? error.code : null;
  }
  
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as ErrorLike;
    return typeof errorObj.code === 'string' ? errorObj.code : null;
  }
  
  return null;
}

/**
 * Creates a user-friendly error message for display
 */
export function formatErrorForUser(error: unknown, context?: string): string {
  const message = getErrorMessage(error);
  const code = getErrorCode(error);
  
  let formatted = message;
  
  if (context) {
    formatted = `${context}: ${formatted}`;
  }
  
  if (code && !formatted.includes(code)) {
    formatted += ` (${code})`;
  }
  
  return formatted;
}

/**
 * Check if an error indicates a network/connectivity issue
 */
export function isNetworkError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  const code = getErrorCode(error);
  
  return (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection') ||
    message.includes('timeout') ||
    code === 'NETWORK_ERROR' ||
    code === 'TIMEOUT'
  );
}

/**
 * Check if an error indicates an authentication issue
 */
export function isAuthError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  const code = getErrorCode(error);
  
  return (
    message.includes('unauthorized') ||
    message.includes('authentication') ||
    message.includes('login') ||
    message.includes('token') ||
    code === 'UNAUTHORIZED' ||
    code === 'FORBIDDEN'
  );
}